import {
  GENERAL_MAX_LENGTH,
  getFieldErrorMessages,
  maxLengthRule,
} from 'app/shared/components/form-input/utilities/form-tools';
import { Opt } from 'app/tools/general.types';
import { sentenceCase } from 'app/tools/utils';
import React from 'react';
import {
  Control,
  FieldValues,
  UseControllerOptions,
} from 'react-hook-form';
import { Merge } from 'type-fest';
import IconLib from '../icon/icon-lib';
import TextInput from './TextInput';
import { InputPropsFor } from './utilities/input.types';

type Props<F extends FieldValues> = Merge<
  InputPropsFor<'TextInput', F>,
  {
    icon?: IconLib;
    control?: InputPropsFor<'TextInput', F>['control'];
  }
>;

export default function FormTextInput<F extends FieldValues>({
  controlName,
  placeholder: placeholderInput,
  label: labelInput,
  optional,
  rules = {},
  control,
  errors,
  defaults = {},
  multiline,
  classicPlaceholder,
  ...props
}: Props<F>): JSX.Element {
  const required = optional ? undefined : 'Required';
  const controller: Opt<UseControllerOptions> = control
    ? {
        control: control as Control,
        name: controlName,
        defaultValue: defaults[controlName] ?? '',
        rules: {
          required,
          ...maxLengthRule(GENERAL_MAX_LENGTH),
          ...rules,
        },
      }
    : undefined;

  const defaultDescriptor = sentenceCase(controlName);
  const placeholder = placeholderInput ?? defaultDescriptor;

  // presence of an icon or classicPlaceholder stops the showing of a
  // label by default, but will still show if a label prop is provided.
  const defaultLabel =
    !props.icon && !classicPlaceholder ? placeholder : undefined;
  let label = labelInput || defaultLabel;
  const suffix =
    !optional && !multiline
      ? ' *'
      : optional && multiline
      ? ' (optional)'
      : '';

  if (
    !classicPlaceholder &&
    placeholder &&
    typeof label === 'string'
  ) {
    label += suffix;
  }

  const errorObj = errors[controlName];
  const errorText = getFieldErrorMessages(errorObj);

  return (
    <TextInput
      greyPlaceholder={!classicPlaceholder}
      {...{ label, placeholder, errorText, controller, multiline }}
      {...props}
    />
  );
}
